/**
 * Currency formatting utilities for Turkish Lira (₺)
 * Formats: 1.299,90 ₺ (dot for thousands separator, comma for decimal separator)
 */

/**
 * Formats a number as Turkish Lira
 * @param {number} amount - The amount to format
 * @param {object} options - Formatting options
 * @param {boolean} options.showSymbol - Whether to show the ₺ symbol (default: true)
 * @param {boolean} options.minimumFractionDigits - Minimum decimal places (default: 2)
 * @param {boolean} options.maximumFractionDigits - Maximum decimal places (default: 2)
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount, options = {}) => {
  const {
    showSymbol = true,
    minimumFractionDigits = 2,
    maximumFractionDigits = 2,
  } = options;

  if (amount === null || amount === undefined || isNaN(amount)) {
    return showSymbol ? '0,00 ₺' : '0,00';
  }

  // Convert to number if it's a string
  const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;

  // Format with Turkish locale
  const formatted = new Intl.NumberFormat('tr-TR', {
    minimumFractionDigits,
    maximumFractionDigits,
  }).format(numAmount);

  return showSymbol ? `${formatted} ₺` : formatted;
};

/**
 * Parses a Turkish Lira formatted string back to a number
 * @param {string} formattedAmount - The formatted currency string
 * @returns {number} The parsed number
 */
export const parseCurrency = (formattedAmount) => {
  if (!formattedAmount) return 0;

  // Remove currency symbol and whitespace
  let cleaned = formattedAmount.replace(/₺/g, '').trim();

  // Replace Turkish formatting (dot = thousands, comma = decimal)
  // First remove dots (thousands separator), then replace comma with dot (decimal)
  cleaned = cleaned.replace(/\./g, '').replace(',', '.');

  const parsed = parseFloat(cleaned);
  return isNaN(parsed) ? 0 : parsed;
};

/**
 * Formats a price range (e.g., "1.000,00 ₺ - 2.000,00 ₺")
 * @param {number} min - Minimum price
 * @param {number} max - Maximum price (optional)
 * @returns {string} Formatted price range
 */
export const formatPriceRange = (min, max = null) => {
  if (max === null) {
    return formatCurrency(min);
  }
  return `${formatCurrency(min)} - ${formatCurrency(max)}`;
};
